export type AgentReadinessFlags = {
  appReady: boolean;
  routeReady: boolean;
  mapReady: boolean;
};

export type AgentContract = {
  version: '1.0.0';
  routeId: string;
  authState: 'anonymous' | 'authenticated';
  activeDrawer: string | null;
  availablePrimaryActions: string[];
  readiness: AgentReadinessFlags;
  updatedAt: string;
};

declare global {
  interface Window {
    __ATLAS_AGENT_CONTRACT__?: AgentContract;
  }
}

const CONTRACT_VERSION: AgentContract['version'] = '1.0.0';

function currentContract(): AgentContract {
  return (
    window.__ATLAS_AGENT_CONTRACT__ ?? {
      version: CONTRACT_VERSION,
      routeId: 'unknown',
      authState: 'anonymous',
      activeDrawer: null,
      availablePrimaryActions: [],
      readiness: {
        appReady: false,
        routeReady: false,
        mapReady: false,
      },
      updatedAt: new Date().toISOString(),
    }
  );
}

export function isAutomationModeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const queryEnabled = params.get('automation') === '1';
  const envEnabled = process.env.REACT_APP_AUTOMATION_MODE === 'true';
  return queryEnabled || envEnabled;
}

export function applyAutomationModeDocumentFlag(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-automation-mode', enabled ? 'true' : 'false');
}

export function emitAutomationEvent(name: string, detail: Record<string, unknown>): void {
  if (!isAutomationModeEnabled() || typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('atlas:automation', {
      detail: {
        name,
        ...detail,
        timestamp: new Date().toISOString(),
      },
    })
  );
}

export function updateAgentContract(
  patch: Omit<Partial<Omit<AgentContract, 'version' | 'updatedAt'>>, 'readiness'> & {
    readiness?: Partial<AgentReadinessFlags>;
  }
): AgentContract {
  if (typeof window === 'undefined') {
    return {
      version: CONTRACT_VERSION,
      routeId: 'unknown',
      authState: 'anonymous',
      activeDrawer: null,
      availablePrimaryActions: [],
      readiness: { appReady: false, routeReady: false, mapReady: false },
      updatedAt: new Date().toISOString(),
    };
  }

  const existing = currentContract();
  const next: AgentContract = {
    ...existing,
    ...patch,
    readiness: {
      ...existing.readiness,
      ...(patch.readiness ?? {}),
    },
    version: CONTRACT_VERSION,
    updatedAt: new Date().toISOString(),
  };

  window.__ATLAS_AGENT_CONTRACT__ = next;

  if (process.env.NODE_ENV === 'production') {
    Object.freeze(window.__ATLAS_AGENT_CONTRACT__);
  }

  return next;
}
